import { memo, useCallback, useLayoutEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import styled from 'styled-components';
import {
  Spinner,
  useSegment,
  useSegmentedControl,
  VisuallyHidden,
} from '@frameio/vapor';
import { theme } from '@frameio/vapor-theme';
import { ShimmerTabLabel } from '../../../shared/components/ShimmerTabLabel';
import { useQuickSearchActiveTab } from '../../../shared/contexts';
import type { SectionId } from '../../../shared/types';
import { LEXICAL_SECTION_ID, NLP_SECTION_ID } from '../../../shared/types';
import TEXT from './generated/TEXT/QuickSearchTabBar';
import styles from './QuickSearchTabLabel.module.css';

TEXT.DEFINE({
  /** Accessible label for the search result category tabs */
  accessibleLabel: 'Search result categories',
  /** Label for the standard results tab */
  standardResults: 'Standard Results',
  /** Accessible label for the standard results tab with count */
  standardResultsWithCount: (count: number): string =>
    `Standard Results (${count})`,
  /** Label for the AI results tab */
  mediaIntelligence: 'AI Results',
  /** Accessible label for the AI results tab with count */
  mediaIntelligenceWithCount: (count: number): string =>
    `AI Results (${count})`,
});

type QuickSearchTabBarProps = {
  standardResultsCount: number;
  mediaIntelligenceResultsCount: number;
  lexicalLoading: boolean;
  nlpLoading: boolean;
  onTabChange?: (sectionId: SectionId) => void;
  /** Stable key for the current search cycle - used to reset the AI shimmer */
  searchCycleKey?: string;
};

type QuickSearchTabOption = {
  ariaLabel: string;
  count: number;
  isAi?: boolean;
  isLoading: boolean;
  label: string;
  searchCycleKey?: string;
  shimmerActive?: boolean;
  value: SectionId;
};

function didAiTabBecomeActive({
  activeTab,
  previousActiveTab,
}: {
  activeTab: SectionId | null;
  previousActiveTab: SectionId | null;
}): boolean {
  return activeTab === NLP_SECTION_ID && previousActiveTab !== NLP_SECTION_ID;
}

// Keeps the segmented control controlled when no tab is selected.
const NO_SELECTION_VALUE = '__none__';

function isSectionId(value: string | null): value is SectionId {
  return value === LEXICAL_SECTION_ID || value === NLP_SECTION_ID;
}

const QuickSearchTabBar = memo(
  ({
    standardResultsCount,
    mediaIntelligenceResultsCount,
    lexicalLoading,
    nlpLoading,
    onTabChange,
    searchCycleKey,
  }: QuickSearchTabBarProps) => {
    const { activeTab, setActiveTab } = useQuickSearchActiveTab();
    const currentSearchCycleKey = searchCycleKey ?? '';
    const [visitedAiQueryKey, setVisitedAiQueryKey] = useState<string | null>(
      null
    );
    const hasVisitedAi = visitedAiQueryKey === currentSearchCycleKey;
    const previousActiveTabRef = useRef<SectionId | null>(activeTab);
    const previousSearchCycleKeyRef = useRef(currentSearchCycleKey);
    const shimmerActive =
      !hasVisitedAi && !nlpLoading && mediaIntelligenceResultsCount > 0;

    const markAiTabAsVisited = useCallback(() => {
      setVisitedAiQueryKey(currentSearchCycleKey);
    }, [currentSearchCycleKey]);

    useLayoutEffect(() => {
      const searchCycleChanged =
        previousSearchCycleKeyRef.current !== currentSearchCycleKey;

      if (
        didAiTabBecomeActive({
          activeTab,
          previousActiveTab: previousActiveTabRef.current,
        }) &&
        !searchCycleChanged
      ) {
        markAiTabAsVisited();
      }

      previousActiveTabRef.current = activeTab;
      previousSearchCycleKeyRef.current = currentSearchCycleKey;
    }, [activeTab, currentSearchCycleKey, markAiTabAsVisited]);

    const { labelProps, radioGroupProps, state } = useSegmentedControl({
      label: TEXT.accessibleLabel,
      onChange: (value) => {
        if (!isSectionId(value)) return;
        if (value === NLP_SECTION_ID) {
          markAiTabAsVisited();
        }
        if (onTabChange) {
          onTabChange(value);
        } else {
          setActiveTab(value);
        }
      },
      orientation: 'horizontal',
      value: activeTab ?? NO_SELECTION_VALUE,
    });

    return (
      <StyledTabBar>
        <SegmentedControlSurface {...radioGroupProps}>
          <VisuallyHidden>
            <span {...labelProps}>{TEXT.accessibleLabel}</span>
          </VisuallyHidden>
          <QuickSearchTab
            option={{
              ariaLabel: lexicalLoading
                ? TEXT.standardResults
                : TEXT.standardResultsWithCount(standardResultsCount),
              count: standardResultsCount,
              isLoading: lexicalLoading,
              label: TEXT.standardResults,
              value: LEXICAL_SECTION_ID,
            }}
            state={state}
          />
          <QuickSearchTab
            option={{
              ariaLabel: nlpLoading
                ? TEXT.mediaIntelligence
                : TEXT.mediaIntelligenceWithCount(
                    mediaIntelligenceResultsCount
                  ),
              count: mediaIntelligenceResultsCount,
              isAi: true,
              isLoading: nlpLoading,
              label: TEXT.mediaIntelligence,
              searchCycleKey,
              shimmerActive,
              value: NLP_SECTION_ID,
            }}
            state={state}
          />
        </SegmentedControlSurface>
      </StyledTabBar>
    );
  }
);

function QuickSearchTab({
  option,
  state,
}: {
  option: QuickSearchTabOption;
  state: ReturnType<typeof useSegmentedControl>['state'];
}) {
  const ref = useRef<HTMLInputElement | null>(null);
  const { inputProps, isSelected } = useSegment(
    {
      'aria-label': option.ariaLabel,
      value: option.value,
    },
    state,
    ref
  );
  const isLoadingSelected = isSelected && option.isLoading;

  return (
    <TabButton $isSelected={isSelected && !option.isLoading}>
      <VisuallyHidden>
        <input {...inputProps} ref={ref} />
      </VisuallyHidden>
      <SegmentLabelRow aria-hidden={true}>
        {option.isAi && !isSelected ? (
          <ShimmerTabLabel
            active={option.shimmerActive}
            searchCycleKey={option.searchCycleKey}
          >
            {option.label}
          </ShimmerTabLabel>
        ) : (
          <span
            className={clsx(styles.tabLabelText, {
              [styles.tabLabelTextActive]: isSelected && !isLoadingSelected,
            })}
          >
            {option.label}
          </span>
        )}
        {option.isLoading ? (
          <CountLoadingSpinner
            aria-label="Loading"
            delay={0}
            enterDuration={0}
            isLoading={true}
            size="x-small"
            variant="default"
          />
        ) : (
          <TabCount $isActive={isSelected}>({option.count})</TabCount>
        )}
      </SegmentLabelRow>
    </TabButton>
  );
}

const StyledTabBar = styled.div`
  width: 100%;
`;

const SegmentedControlSurface = styled.div`
  align-items: stretch;
  background: ${theme.colors.components.segmentedControl.background.default};
  border-radius: ${theme.radii[8]};
  display: flex;
  gap: ${theme.spaces[2]};
  height: ${theme.componentSizes[36]};
  padding: ${theme.spaces[4]};
  width: 100%;
`;

const TabButton = styled.label<{ $isSelected: boolean }>`
  align-items: center;
  background: ${({ $isSelected }) =>
    $isSelected
      ? theme.colors.components.segmentedControl.background.interactive.active
      : 'transparent'};
  border-radius: ${theme.radii[6]};
  cursor: pointer;
  display: flex;
  flex: 1 0 0;
  justify-content: center;
  min-width: 0;
  padding: ${theme.spaces[4]};
  transition:
    background-color ${theme.transitions.durations['150']}
      ${theme.transitions.eases.primary},
    color ${theme.transitions.durations['150']}
      ${theme.transitions.eases.primary};

  &:has(input:focus-visible) {
    box-shadow: inset 0 0 0 1px ${theme.colors.border.interactive.focused};
  }
`;

const SegmentLabelRow = styled.span`
  align-items: center;
  display: inline-flex;
  gap: ${theme.spaces[4]};
  justify-content: center;
  min-width: 0;
  user-select: none;
`;

const CountLoadingSpinner = styled(Spinner)`
  display: flex;
  flex-shrink: 0;
  justify-content: center;
  min-width: ${theme.spaces[16]};
`;
const TAB_TEXT_FONT_SIZE = theme.fontSizes[70];
const TAB_TEXT_LINE_HEIGHT = theme.lineHeights[100];
const TAB_TEXT_TRANSITION = `color ${theme.transitions.durations['150']} ${theme.transitions.eases.primary}`;

/** Count number in parens after the label. Swapped for a Spinner during loading. */
const TabCount = styled.span<{ $isActive: boolean }>`
  color: ${({ $isActive }) =>
    $isActive ? theme.colors.text.primary : theme.colors.text.secondary};
  flex-shrink: 0;
  font-size: ${TAB_TEXT_FONT_SIZE};
  font-weight: ${theme.fontWeights.medium};
  line-height: ${TAB_TEXT_LINE_HEIGHT};
  transition: ${TAB_TEXT_TRANSITION};

  label:hover &,
  label.hover & {
    color: ${theme.colors.text.primary};
  }
`;

QuickSearchTabBar.displayName = 'QuickSearchTabBar';

export { QuickSearchTabBar };
